import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useFamily } from '../../contexts/FamilyContext';
import toast from 'react-hot-toast';
import RecentChatsAirbnb from './RecentChatsAirbnb';
import ConversationStartersAirbnb from './ConversationStartersAirbnb';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faMicrophone, faLink, faStop } from '@fortawesome/free-solid-svg-icons';

export default function ChatSection({ experiences = [], currentExperience = null }) {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [selectedExperience, setSelectedExperience] = useState(currentExperience);
  const [showExperienceDropdown, setShowExperienceDropdown] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recognition, setRecognition] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { family, familyId } = useFamily();
  const experienceDropdownRef = useRef(null);
  const textareaRef = useRef(null);

  // Initialize speech recognition
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition) {
        try {
          const recognitionInstance = new SpeechRecognition();
          recognitionInstance.continuous = false;
          recognitionInstance.interimResults = false;
          recognitionInstance.lang = 'en-US';

          recognitionInstance.onresult = (event) => {
            const transcript = event.results[0][0].transcript;
            setMessage(prev => prev + (prev ? ' ' : '') + transcript);
            setIsRecording(false);
          };

          recognitionInstance.onerror = (event) => {
            console.error('Speech recognition error:', event.error);
            setIsRecording(false);
            if (event.error === 'not-allowed') {
              toast.error('Microphone access denied. Please enable it in your browser settings.');
            } else {
              toast.error('Speech recognition failed. Please try again.');
            }
          };

          recognitionInstance.onend = () => {
            setIsRecording(false);
          };

          setRecognition(recognitionInstance);
        } catch (error) {
          console.error('Error initializing speech recognition:', error);
        }
      }
    }
  }, []);

  // Update selectedExperience when currentExperience changes
  useEffect(() => {
    setSelectedExperience(currentExperience);
  }, [currentExperience]);

  // Auto-resize textarea based on content
  const adjustTextareaHeight = (textarea) => {
    if (!textarea) return;
    // Reset height to get accurate scrollHeight
    textarea.style.height = 'auto';
    // Set height based on scrollHeight, with min and max constraints
    const minHeight = 240; // min-h-[240px] (doubled from 120px)
    const maxHeight = 600; // max height before scrolling (doubled from 300px)
    const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight);
    textarea.style.height = `${newHeight}px`;
  };

  // Adjust height when message changes (for programmatic updates like voice input)
  useEffect(() => {
    if (textareaRef.current) {
      adjustTextareaHeight(textareaRef.current);
    }
  }, [message]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (experienceDropdownRef.current && !experienceDropdownRef.current.contains(event.target)) {
        setShowExperienceDropdown(false);
      }
    };

    if (showExperienceDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showExperienceDropdown]);

  const handleStartRecording = () => {
    if (!recognition) {
      toast.error('Speech recognition is not supported in your browser.');
      return;
    }

    try {
      recognition.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recognition:', error);
      toast.error('Failed to start recording. Please try again.');
    }
  };

  const handleStopRecording = () => {
    if (recognition && isRecording) {
      recognition.stop();
      setIsRecording(false);
    }
  };

  const handleLinkExperience = (experience) => {
    setSelectedExperience(experience);
    setShowExperienceDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim() || sending) return;

    setSending(true);

    try {
      // Create new conversation with first message
      const conversationRef = await addDoc(collection(db, 'conversations'), {
        familyId: familyId,
        userId: user.uid,
        title: message.substring(0, 50) + (message.length > 50 ? '...' : ''),
        messages: [
          {
            role: 'user',
            content: message,
            timestamp: new Date()
          }
        ],
        familyContext: {
          familyName: family?.name,
          kids: family?.kids || [],
          identity: family?.onboarding?.identity,
          goals: family?.onboarding?.goals
        },
        currentExperience: selectedExperience
          ? {
              id: selectedExperience.id,
              type: selectedExperience.type,
              title: selectedExperience.title,
              currentPhase: selectedExperience.currentPhase
            }
          : null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Navigate to chat page (which will auto-send to AI)
      navigate(`/chats/${conversationRef.id}?autoSend=true`);
      
    } catch (error) {
      console.error('Error creating chat:', error);
      toast.error('Failed to start chat. Please try again.');
      setSending(false);
    }
  };

  const handleStarterClick = async (starterPrompt) => {
    if (sending) return;
    
    setSending(true);

    try {
      // Create new conversation with starter message
      const conversationRef = await addDoc(collection(db, 'conversations'), {
        familyId: familyId,
        userId: user.uid,
        title: starterPrompt.substring(0, 50) + (starterPrompt.length > 50 ? '...' : ''),
        messages: [
          {
            role: 'user',
            content: starterPrompt,
            timestamp: new Date()
          }
        ],
        familyContext: {
          familyName: family?.name,
          kids: family?.kids || [],
          identity: family?.onboarding?.identity,
          goals: family?.onboarding?.goals
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Navigate to chat page (which will auto-send to AI)
      navigate(`/chats/${conversationRef.id}?autoSend=true`);
      
    } catch (error) {
      console.error('Error creating chat:', error);
      toast.error('Failed to start chat. Please try again.');
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Chat Input - Google Search Bar Style with Drop Shadow */}
      <form onSubmit={handleSubmit} className="max-w-2xl mx-auto">
        <div className="relative">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onInput={(e) => adjustTextareaHeight(e.target)}
            placeholder="Ask about planning a celebration, get recipe ideas, or just chat..."
            className="w-full min-h-[240px] max-h-[600px] pt-5 pl-5 pr-20 pb-20 border border-border rounded-xl focus:outline-none focus:border-primary focus:shadow-lg transition-all text-base text-text-primary placeholder:text-text-secondary/60 resize-none shadow-md overflow-y-auto"
            disabled={sending || isRecording}
          />
          
          {/* Link to Experience - Bottom Left */}
          {experiences.length > 0 && (
            <div className="absolute bottom-4 left-4" ref={experienceDropdownRef}>
              <button
                type="button"
                onClick={() => setShowExperienceDropdown(!showExperienceDropdown)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 ${
                  selectedExperience
                    ? 'bg-primary text-white'
                    : 'bg-white/90 backdrop-blur-sm text-text-secondary hover:bg-white border border-border/50'
                }`}
              >
                <FontAwesomeIcon icon={faLink} className="text-xs" />
                <span className="max-w-[120px] truncate">
                  {selectedExperience ? selectedExperience.title : 'Link Experience'}
                </span>
                {selectedExperience && (
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedExperience(null);
                    }}
                    className="ml-1 hover:bg-white/20 rounded px-1 cursor-pointer inline-flex items-center justify-center"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        setSelectedExperience(null);
                      }
                    }}
                  >
                    ×
                  </span>
                )}
              </button>

              {showExperienceDropdown && (
                <div className="absolute bottom-full left-0 mb-2 w-64 bg-white rounded-lg shadow-lg border border-border z-50 max-h-60 overflow-y-auto">
                  <div className="p-2">
                    <div className="text-xs font-semibold text-text-secondary px-2 py-1 mb-1">
                      Link to Experience
                    </div>
                    {experiences.map((exp) => (
                      <button
                        key={exp.id}
                        type="button"
                        onClick={() => handleLinkExperience(exp)}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-surface transition-colors text-sm text-text-primary"
                      >
                        {exp.title || 'Untitled Experience'}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedExperience(null);
                        setShowExperienceDropdown(false);
                      }}
                      className="w-full text-left px-3 py-2 rounded-lg hover:bg-surface transition-colors text-sm text-text-secondary"
                    >
                      No link
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="absolute bottom-4 right-4 flex items-center gap-2">
            {/* Audio Button */}
            {recognition && (
              <button
                type="button"
                onClick={isRecording ? handleStopRecording : handleStartRecording}
                disabled={sending}
                className={`p-2.5 rounded-lg transition-all ${
                  isRecording
                    ? 'bg-red-500 text-white hover:bg-red-600'
                    : 'bg-white/90 backdrop-blur-sm text-text-primary hover:bg-white border border-border/50'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
                title={isRecording ? 'Stop recording' : 'Start voice input'}
              >
                <FontAwesomeIcon 
                  icon={isRecording ? faStop : faMicrophone} 
                  className={`h-5 w-5 ${isRecording ? 'animate-pulse' : ''}`}
                />
              </button>
            )}
            <button
              type="submit"
              disabled={!message.trim() || sending}
              className="p-2.5 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
            >
              {sending ? (
                <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              ) : (
                <FontAwesomeIcon icon={faPaperPlane} className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>
      </form>

      {/* Recent Chats */}
      <RecentChatsAirbnb limit={3} />

      {/* Conversation Starters */}
      <ConversationStartersAirbnb limit={3} onStarterClick={handleStarterClick} />
    </div>
  );
}
